// The SCHEDULER MONTH CARD (Scheduler Screen v2, the "Signal" re-layout) — the serif month head with its
// Previous/Next navigation, the day-name row, and the padded Monday-start grid of day cells.
//
// Split out of RoutineCalendar.tsx when month navigation arrived: the calendar was pinned to the current
// month, so a routine whose next occurrence fell in the following month could be projected by the server
// and never seen. Navigation moves a WINDOW over entries the projection already carries; it computes no
// recurrence and fetches nothing (G-110). A month the projection says nothing about renders as a grid of
// empty days and says so in the summary line — never a padded or invented one.
//
// Tokens only (G-181); no internal id renders (G-197).

import { Icon } from '../../app/Icon.tsx';
import { localIsoDate, monthKey } from './scheduler-format.ts';
import type { DayEntry } from './routine-day-entries.ts';

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** The anchored month's cell dates, padded to whole Monday-start weeks (out-of-month cells muted). */
export function monthCells(
  anchor: Date,
): readonly { readonly date: Date; readonly inMonth: boolean }[] {
  const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
  const last = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
  const lead = (first.getDay() + 6) % 7;
  const tail = (7 - ((lead + last.getDate()) % 7)) % 7;
  const cells: { date: Date; inMonth: boolean }[] = [];
  for (let index = -lead; index < last.getDate() + tail; index++) {
    const date = new Date(anchor.getFullYear(), anchor.getMonth(), 1 + index);
    cells.push({ date, inMonth: date.getMonth() === anchor.getMonth() });
  }
  return cells;
}

/** Count the placed entries across the anchored month's OWN days (the honest month summary line). */
function inMonthRunCount(
  cells: ReturnType<typeof monthCells>,
  byDay: ReadonlyMap<string, readonly DayEntry[]>,
): number {
  return cells
    .filter((cell) => cell.inMonth)
    .reduce((total, cell) => total + (byDay.get(localIsoDate(cell.date))?.length ?? 0), 0);
}

/** One day cell — the mono day number, the Today ring, and up to three placed entries. */
function DayCell({
  date,
  inMonth,
  entries,
  isToday,
  isSelected,
  onSelect,
}: {
  readonly date: Date;
  readonly inMonth: boolean;
  readonly entries: readonly DayEntry[];
  readonly isToday: boolean;
  readonly isSelected: boolean;
  readonly onSelect: (day: string) => void;
}): React.JSX.Element {
  const day = localIsoDate(date);
  const shown = entries.slice(0, 3);
  const cls = `rc-cell${inMonth ? '' : ' out'}${isToday ? ' today' : ''}${isSelected ? ' sel' : ''}`;
  return (
    <button
      type="button"
      className={cls}
      data-cal-day={day}
      disabled={!inMonth}
      onClick={() => onSelect(day)}
      aria-label={`Day ${String(date.getDate())}: ${String(entries.length)} entries`}
      aria-pressed={isSelected}
    >
      <span className="rc-cell-head">
        <span className="rc-num">{date.getDate()}</span>
        {isToday ? <span className="rc-tag">Today</span> : null}
      </span>
      {shown.map((entry) => (
        <span key={entry.instant + entry.name} className="rc-chip" data-tone={entry.tone}>
          <span className="bar" aria-hidden="true" />
          <span className="body">
            <span className="nm">{entry.name}</span>
            <span className="tm">{entry.time}</span>
          </span>
        </span>
      ))}
      {entries.length > shown.length ? (
        <span className="rc-more">+{entries.length - shown.length} more</span>
      ) : null}
    </button>
  );
}

/** The Previous / Next month pair — client view-state only; nothing is fetched and nothing is computed. */
function MonthNav({
  onStepMonth,
}: {
  readonly onStepMonth: (step: number) => void;
}): React.JSX.Element {
  return (
    <div className="rc-nav">
      <button
        type="button"
        className="rc-navbtn"
        aria-label="Previous month"
        onClick={() => onStepMonth(-1)}
      >
        <Icon name="chevleft" size={14} />
      </button>
      <button
        type="button"
        className="rc-navbtn"
        aria-label="Next month"
        onClick={() => onStepMonth(1)}
      >
        <Icon name="chevron" size={14} />
      </button>
    </div>
  );
}

/** The month head: the serif month name, the Previous/Next pair, and the honest placement summary. */
function MonthHead({
  anchor,
  placed,
  onStepMonth,
}: {
  readonly anchor: Date;
  readonly placed: number;
  readonly onStepMonth: (step: number) => void;
}): React.JSX.Element {
  return (
    <div className="rc-head">
      <h2 className="rc-title">
        {anchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}
      </h2>
      <MonthNav onStepMonth={onStepMonth} />
      <span className="spacer" aria-hidden="true" />
      {/* What the projection actually placed in THIS month. A month it says nothing about says
          nothing — it never borrows a neighbouring month's tally to look populated. */}
      <span className="rc-sum">
        {placed === 0
          ? 'nothing recorded or projected this month'
          : `${String(placed)} entries recorded or projected this month`}
      </span>
    </div>
  );
}

/** What the month card renders. `anchor` is any date inside the month being shown. */
export interface RoutineMonthCardProps {
  readonly anchor: Date;
  readonly todayIso: string;
  readonly byDay: ReadonlyMap<string, readonly DayEntry[]>;
  readonly selectedDay: string;
  readonly onSelectDay: (day: string) => void;
  readonly onStepMonth: (step: number) => void;
}

/** The month card: the serif month head + navigation, the day-name row, and the padded day-cell grid. */
export function RoutineMonthCard({
  anchor,
  todayIso,
  byDay,
  selectedDay,
  onSelectDay,
  onStepMonth,
}: RoutineMonthCardProps): React.JSX.Element {
  const cells = monthCells(anchor);
  const placed = inMonthRunCount(cells, byDay);
  return (
    <div className="rc-card" data-cal-month={monthKey(anchor)}>
      <MonthHead anchor={anchor} placed={placed} onStepMonth={onStepMonth} />
      <div className="rc-days" aria-hidden="true">
        {DAY_NAMES.map((name) => (
          <div key={name} className="rc-day">
            {name}
          </div>
        ))}
      </div>
      <div className="rc-grid">
        {cells.map((cell) => {
          const day = localIsoDate(cell.date);
          return (
            <DayCell
              key={day}
              date={cell.date}
              inMonth={cell.inMonth}
              entries={byDay.get(day) ?? []}
              isToday={day === todayIso}
              isSelected={day === selectedDay}
              onSelect={onSelectDay}
            />
          );
        })}
      </div>
    </div>
  );
}
